import { forwardRef } from '@heroui/system';
import { Avatar, AvatarProps, extendVariants } from '@heroui/react';
import {
  CarGroundIcon,
  ClinicalSpecialistIcon,
  DeviceIcon,
  FacilityIcon,
  FallbackAvatarIcon,
  PlaneIcon,
  ScsIcon,
  SurgeonIcon,
} from '@icons';
import { useMemo, useState, useEffect, JSX } from 'react';
import { MdOutlineInbox } from 'react-icons/md';

export const CustomAvatar = extendVariants(Avatar, {
  variants: {
    color: {
      default: {
        base: 'bg-base-40 text-base-10',
        name: 'text-current font-normal leading-tight',
      },
      primary: {
        base: 'bg-base-40 text-base-10',
        name: 'text-current font-normal leading-tight',
      },
      secondary: {
        base: 'bg-white text-base-5',
        name: 'text-current font-normal leading-tight',
      },
    },
    size: {
      xxl: {
        base: 'w-24 h-24',
        fallback: '[&>svg]:w-18 [&>svg]:h-18 text-[2.75rem] leading-none',
      },
      xl: {
        base: 'w-12 h-12',
        fallback: '[&>svg]:w-8 [&>svg]:h-8 text-xl-r leading-none',
      },
      lg: {
        base: 'w-8 h-8',
        fallback: '[&>svg]:w-6 [&>svg]:h-6 text-m-r leading-none',
      },
      md: {
        base: 'w-6 h-6',
        fallback: '[&>svg]:w-4 [&>svg]:h-4 text-s-r leading-none',
      },
      sm: {
        base: 'w-4 h-4',
        fallback: '[&>svg]:w-3 [&>svg]:h-3 text-s-r leading-none',
      },
    },
    radius: {
      full: {
        base: 'rounded-full',
      },
      xxl: {
        base: 'rounded-[1.75rem]',
      },
      xl: {
        base: 'rounded-2xl',
      },
      lg: {
        base: 'rounded-xl',
      },
      md: {
        base: 'rounded-lg',
      },
      sm: {
        base: 'rounded',
      },
    },
    isBordered: {
      true: {
        base: 'ring-base-70',
      },
    },
  },
  defaultVariants: {
    color: 'default',
    size: 'xxl',
    radius: 'full',
  },
  compoundVariants: [
    {
      isBordered: true,
      color: 'default',
      class: 'ring-base-70 ring-offset-0',
    },
    {
      isBordered: true,
      color: 'primary',
      class: 'ring-base-10 ring-offset-black ring-offset-0',
    },
    {
      isBordered: true,
      size: 'xxl',
      class: 'ring-2',
    },
    {
      isBordered: true,
      size: 'xl',
      class: 'ring-2',
    },
    {
      isBordered: true,
      size: 'lg',
      class: 'ring-2',
    },
    {
      isBordered: true,
      size: 'md',
      class: 'ring-[0.09375rem]',
    },
    {
      isBordered: true,
      size: 'sm',
      class: 'ring-1',
    },
  ],
});

export type CustomAvatarProps = Omit<AvatarProps, 'size' | 'radius' | 'name' | 'ref' | 'src'> & {
  src?: string;
  size?: 'xxl' | 'xl' | 'lg' | 'md' | 'sm';
  radius?: 'full' | 'xxl' | 'xl' | 'lg' | 'md' | 'sm';
  color?: 'default' | 'primary' | 'secondary';
  type?:
    | 'user'
    | 'device'
    | 'scs'
    | 'facility'
    | 'transport'
    | 'car'
    | 'supply'
    | 'clinical-specialist'
    | 'retrieval-surgeon';
  name?: string | null;
  fallbackInitials?: boolean;
  showFullInitials?: boolean;
};

const AvatarWrapper = forwardRef<'span', CustomAvatarProps>((props, ref) => {
  const {
    name,
    fallbackInitials = true,
    showFullInitials,
    size = 'md',
    color,
    type = 'user',
    isBordered,
    children,
    src,
    isDisabled,
    className,
    classNames,
    onError,
    ...restProps
  } = props;

  const [hasLoadError, setHasLoadError] = useState(false);

  useEffect(() => {
    setHasLoadError(false);
  }, [src]);

  const safeName = name === null ? undefined : name;

  const getInitials = (fullName: string) => {
    const trimmedName = fullName.trim().replace(/\s+/g, ' ');

    if ((size === 'sm' || size === 'md') && !showFullInitials) {
      return trimmedName[0]?.toUpperCase() || '';
    }

    const parts = trimmedName.split(' ');

    if (parts.length > 1) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }

    return trimmedName.slice(0, 2).toUpperCase();
  };

  const getFallback = (): JSX.Element => {
    switch (type) {
      case 'user':
        return <FallbackAvatarIcon />;
      case 'device':
        return <DeviceIcon />;
      case 'scs':
        return <ScsIcon />;
      case 'transport':
        return <PlaneIcon />;
      case 'car':
        return <CarGroundIcon />;
      case 'supply':
        return <MdOutlineInbox />;
      case 'clinical-specialist':
        return <ClinicalSpecialistIcon />;
      case 'retrieval-surgeon':
        return <SurgeonIcon />;
      default:
        return <FacilityIcon />;
    }
  };

  const stylesForNonFallbackInitials = useMemo(() => {
    const defaultStyle = {
      fallback: 'tracking-tight',
    };

    if (type !== 'facility' && !fallbackInitials) {
      return {};
    }

    if (type !== 'facility') {
      return defaultStyle;
    }

    const facilityStyleMap = {
      xxl: { fallback: 'text-2xl! font-bold' },
      xl: { fallback: 'text-xs! font-bold' },
      lg: { fallback: 'text-[0.5rem]! font-bold' },
      md: { fallback: 'text-[0.375rem]! font-bold' },
      sm: { fallback: 'text-[0.28rem]! font-bold' },
    };

    return facilityStyleMap[size] || defaultStyle;
  }, [type, fallbackInitials, size]);

  const displayName = useMemo(() => {
    const initials = !!name?.trim() && name !== 'Unnamed Contact' ? getInitials(name) : getFallback();

    if (type === 'facility' && isDisabled) {
      return getFallback();
    }

    if (['transport', 'car', 'device'].includes(type)) {
      return getFallback();
    }

    return fallbackInitials ? initials : name || getFallback();
  }, [name, fallbackInitials, type, isDisabled, size, showFullInitials]);

  return (
    <CustomAvatar
      ref={ref}
      src={hasLoadError ? undefined : src}
      name={safeName}
      size={size}
      color={color}
      isBordered={isBordered}
      fallback={displayName}
      onError={() => {
        setHasLoadError(true);
        onError?.();
      }}
      {...restProps}
      className={['shrink-0', className].filter(Boolean).join(' ')}
      classNames={{
        ...classNames,
        ...stylesForNonFallbackInitials,

        base: ['overflow-hidden rounded-full', classNames?.base].filter(Boolean).join(' '),

        img: ['rounded-full object-cover', classNames?.img].filter(Boolean).join(' '),
      }}
    />
  );
});

AvatarWrapper.displayName = 'CustomAvatar';

export default AvatarWrapper;
